import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { FloatLabel } from 'primereact/floatlabel';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputMask, InputMaskChangeEvent } from 'primereact/inputmask';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import React, { useRef, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { TokenService } from '../services/TokenService';
import TeamGuide from './TeamGuide';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';

export enum TokenStatus {
    Created = 'created',
    Sent = 'sent',
    Filled = 'filled',
    Exported = 'exported'
};

export interface Token {
    id: string;
    name: string;
    email: string;
    telephone?: string;
    internalComments?: string;
    createdAt: Date;
    exportedAt?: Date;
    filledAt?: Date;
    emailSentAt?: Date;
    status: TokenStatus;
    partnerId?: string;
    partnerName?: string;
    company?: string;
    occupation?: string;
    dueDate?: Date;
    fileUploaded?: boolean;
    sendWelcomeEmail?: boolean;
}

interface EditTokenProps {
    token: Partial<Token>;
    unauthenticated?: boolean;
    onSave: (token: Token) => void;
}

export const EditToken: React.FC<EditTokenProps> = ({ token, onSave, unauthenticated }) => {
    const [formData, setFormData] = useState<Partial<Token>>(token);
    const [emailError, setEmailError] = useState<string | null>(null);
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const { isAdmin } = useAuth();
    const [uploading, setUploading] = useState(false);
    // File upload handler for admins using react-dropzone
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (!formData.id || acceptedFiles.length === 0) return;
        const file = acceptedFiles[0];
        setUploading(true);
        try {
            await TokenService.uploadResultFile(formData.id, file);
            toast.current?.show({ severity: 'success', summary: t('editToken.uploadSuccess') || 'Upload successful', life: 2000 });
            setFormData(t => ({ ...t, fileUploaded: true }));
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: t('editToken.uploadError') || 'Upload failed', life: 2000 });
        } finally {
            setUploading(false);
        }
    }, [formData.id, t, setFormData]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': [] },
        maxFiles: 1,
        disabled: uploading
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | InputMaskChangeEvent) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        if (name === 'email') {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setEmailError(emailPattern.test(value!) ? null : t('editToken.invalidEmail'));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData as Token);
    };

    const copyLink = `${window.location.origin}/questionare/${formData.id}`;

    const handleCopy = (success: boolean) => {
        if (success) {
            toast.current?.show({ severity: 'success', summary: t('editToken.copySuccess'), life: 1000 });
        } else {
            toast.current?.show({ severity: 'error', summary: t('editToken.copyError'), life: 1000 });
        }
    };

    const formatDate = (date?: Date) => date ? dayjs(date).format('L') : '-';

    const isFormValid = () => {
        return formData.name
            && formData.email
            && !emailError;
    };

    return (
        <form onSubmit={handleSubmit} className="p-fluid grid formgrid">
            <Toast ref={toast} />
            <div className="field col-12 md:col-6" style={{ marginTop: '6px' }}>
                <FloatLabel>
                    <InputText id="name" name="name" value={formData.name ?? ''} onChange={handleInputChange} required />
                    <label htmlFor="name">{t('editToken.name')}</label>
                </FloatLabel>
            </div>
            <div className="field col-12 md:col-6" style={{ marginTop: '6px' }}>
                <FloatLabel>
                    <label htmlFor="email">{t('editToken.email')}</label>
                    <InputText id="email" name="email" value={formData.email ?? ''} onChange={handleInputChange} required className={emailError ? 'p-invalid' : ''} />
                    {emailError && <small className="p-error">{emailError}</small>}
                </FloatLabel>
            </div>
            <div className="field col-12 md:col-6" style={{ marginTop: '6px' }}>
                <FloatLabel>
                    <label htmlFor="telephone">{t('editToken.telephone')}</label>
                    <InputMask id="telephone" name="telephone" mask="99/9999?99999" value={formData.telephone ?? ''} onChange={handleInputChange} />
                </FloatLabel>
            </div>
            <div className="field col-12 md:col-6" style={{ marginTop: '6px' }}>
                <FloatLabel>
                    <label htmlFor="partnerName">{t('editToken.partnerName')}</label>
                    <InputText id="partnerName" name="partnerName" value={formData.partnerName ?? ''} readOnly />
                </FloatLabel>
            </div>
            <TeamGuide>
                {isAdmin && !unauthenticated && (
                    <>
                        <div className="field col-12 md:col-6" style={{ marginTop: '6px' }}>
                            <FloatLabel>
                                <label htmlFor="company">{t('editToken.company') || 'Company'}</label>
                                <InputText id="company" name="company" value={formData.company ?? ''} onChange={handleInputChange} />
                            </FloatLabel>
                        </div>
                        <div className="field col-12 md:col-6" style={{ marginTop: '6px' }}>
                            <FloatLabel>
                                <label htmlFor="occupation">{t('editToken.occupation') || 'Occupation'}</label>
                                <InputText id="occupation" name="occupation" value={formData.occupation ?? ''} onChange={handleInputChange} />
                            </FloatLabel>
                        </div>
                    </>
                )}

                <div className="field col-12 md:col-6" style={{ marginTop: '6px' }}>
                    <FloatLabel>
                        <label htmlFor="dueDate">{t('editToken.dueDate') || 'Due Date'}</label>
                        <Calendar
                            id="dueDate"
                            name="dueDate"
                            value={formData.dueDate ? dayjs(formData.dueDate).toDate() : null}
                            onChange={e => {
                                console.log('Due date changed:', e.value);
                                setFormData({
                                    ...formData,
                                    dueDate: e.value ? dayjs(e.value).toDate() : undefined
                                });
                            }}
                            showIcon
                            showButtonBar
                            minDate={(() => { const d = dayjs().add(1, 'day').startOf('day').toDate(); return d; })()}
                        />
                    </FloatLabel>
                </div>
            </TeamGuide>
            <div className="field col-12" style={{ marginTop: '6px' }}>
                <FloatLabel>
                    <label htmlFor="internalComments">{t('editToken.internalComments')}</label>
                    <InputTextarea id="internalComments" name="internalComments" value={formData.internalComments ?? ''} onChange={handleInputChange} rows={3} autoResize />
                </FloatLabel>
            </div>
            {!formData.id && (
                <div className="field col-12" style={{ marginTop: '6px' }}>
                    <div className="flex align-items-center">
                        <Checkbox
                            inputId="sendWelcomeEmail"
                            checked={!!formData.sendWelcomeEmail}
                            onChange={(e) => {
                                setFormData(current => ({
                                    ...current,
                                    sendWelcomeEmail: !!e.checked
                                }));
                            }}
                        />
                        <label htmlFor="sendWelcomeEmail" className="ml-2">
                            {t('editToken.sendWelcomeEmail')}
                        </label>
                    </div>
                </div>
            )}
            {formData.id && <>
                {isAdmin && !formData.fileUploaded && (
                    <div className="field col-12" style={{ marginTop: '6px' }}>
                            <div
                                {...getRootProps()}
                                style={{
                                    border: '2px dashed #888',
                                    marginTop: 8,
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    padding: 20,
                                    textAlign: 'center',
                                    background: isDragActive ? 'rgb(127, 127, 127, 0.5)' : 'inherit',
                                    transition: 'background 0.4s ease',
                                }}
                            >

                                <input {...getInputProps()}

                                    id="resultFileUpload"
                                    disabled={uploading} />
                                {uploading ?
                                    (t('editToken.uploading'))
                                    : isDragActive ?
                                        (t('editToken.dropHere'))
                                        : (t('editToken.dragDropOrClick'))}
                            </div>
                    </div>
                )}

                {/* Download link visible to everyone if a result file exists */}
                {formData.fileUploaded && (
                    <div className="field col-12" style={{ marginTop: '6px' }}>
                        <Button type="button" label={t('editToken.downloadResult')} icon="pi pi-download" onClick={async () => {
                            if (!formData.id) return;
                            try {
                                const blob = await TokenService.downloadResultFile(formData.id);
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${formData.name || 'result'}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                window.URL.revokeObjectURL(url);
                            } catch (err) {
                                toast.current?.show({ severity: 'error', summary: t('editToken.downloadError'), life: 2000 });
                            }
                        }} />
                    </div>
                )}

                <div className="field col-12 md:col-6 lg:col-3" style={{ marginTop: '6px' }}>
                    <FloatLabel>
                        <label htmlFor="createdAt">{t('editToken.createdAt')}</label>
                        <InputText id="createdAt" name="createdAt" value={formatDate(formData.createdAt)} readOnly />
                    </FloatLabel>
                </div>
                <div className="field col-12 md:col-6 lg:col-3" style={{ marginTop: '6px' }}>
                    <FloatLabel>
                        <label htmlFor="emailSentAt">{t('editToken.emailSentAt')}</label>
                        <InputText id="emailSentAt" name="emailSentAt" value={formatDate(formData.emailSentAt)} readOnly />
                    </FloatLabel>
                </div>
                <div className="field col-12 md:col-6 lg:col-3" style={{ marginTop: '6px' }}>
                    <FloatLabel>
                        <label htmlFor="filledAt">{t('editToken.filledAt')}</label>
                        <InputText id="filledAt" name="filledAt" value={formatDate(formData.filledAt)} readOnly />
                    </FloatLabel>
                </div>
                <div className="field col-12 md:col-6 lg:col-3" style={{ marginTop: '6px' }}>
                    <FloatLabel>
                        <label htmlFor="exportedAt">{t('editToken.exportedAt')}</label>
                        <InputText id="exportedAt" name="exportedAt" value={formatDate(formData.exportedAt)} readOnly />
                    </FloatLabel>
                </div>
                <div className="field col-12" style={{ marginTop: '6px' }}>
                    <FloatLabel>
                        <label htmlFor="copyLink">{t('editToken.copyLink')}</label>
                        <IconField>

                            <InputText id="copyLink" value={copyLink} readOnly />
                            <CopyToClipboard text={copyLink} onCopy={(text, result) => handleCopy(result)}>
                                <InputIcon className="pi pi-copy" type="button" />
                            </CopyToClipboard>
                        </IconField>
                    </FloatLabel>
                </div>
            </>}
            <div className="col-12" style={{ marginTop: '6px' }}>
                <Button type="submit" label={t('editToken.submit')} disabled={!isFormValid()} />
            </div>
        </form>
    );
};
